/**
 * SVG source-stash — per-tab IndexedDB store of the original SVG text
 * plus the editor-to-SVG node-index mapping. Foundation for the
 * lossless round-trip story when sub-piece #4 (SVG export) lands: a
 * user opens an SVG containing features the editor flattens to raster
 * (text, filters, masks, gradients, etc.); the stash preserves the
 * original verbatim so the export path can restore those layers.
 *
 * Mirror of `psd-source-stash.ts` — same gzip-on-write / quota-pre-
 * check pattern, same documentId keying, same transaction semantics
 * via the shared `openImageEditorDb`. The two stashes coexist as
 * separate IDB stores (`psd-stashes` and `svg-stashes`) — a given tab
 * only populates one of them (PSD or SVG, never both), but the unified
 * naming keeps `clearSession`'s single-transaction wipe semantics
 * symmetric across formats.
 *
 * SVG text is much smaller than PSD bytes typically (KB rather than
 * MB), but the gzip + quota plumbing is identical to keep the two
 * modules structurally aligned — a future refactor can generalize
 * both into a single "source-stash" module if a third format arrives.
 *
 * The export path (sub-piece #4) will pair this stash with a re-parse
 * of the stored SVG to recover features the editor flattened.
 */

import { openImageEditorDb, SESSION_STORE_NAMES } from './session-store';

/** Maps editor LayerNode.id → which SVG node it originated from.
 *  `svgNodeIndex` is the depth-first ordinal in the original SVG's
 *  tree (the parent SVG document, not the editor's tree), computed
 *  once at import time. The editor's tree is authoritative on export;
 *  the index simply locates the source XML element in the re-parsed
 *  SVG document at export time. */
export interface SvgNodeOrigin {
  svgNodeIndex: number;
}

/** Persisted shape of a stash entry. The SVG text is gzip-compressed
 *  for symmetry with the PSD path — typical SVGs are small enough
 *  that compression overhead is negligible, but a complex
 *  Illustrator-exported SVG can easily hit 5+ MB. */
export interface StoredSvgStash {
  documentId: string;
  svgTextGz: ArrayBuffer;
  uncompressedSize: number;
  layerOriginById: Record<string, SvgNodeOrigin>;
  originalWidth: number;
  originalHeight: number;
  savedAt: number;
}

/** In-memory shape returned to callers — `svgText` is decompressed. */
export interface SvgStash {
  documentId: string;
  svgText: string;
  uncompressedSize: number;
  compressedSize: number;
  layerOriginById: Record<string, SvgNodeOrigin>;
  originalWidth: number;
  originalHeight: number;
  savedAt: number;
}

/** Thrown when the storage budget can't accommodate the SVG import.
 *  Caller surfaces a clear toast and refuses the import — we'd
 *  rather refuse than leave a partial stash behind. */
export class SvgStashQuotaError extends Error {
  constructor(
    public readonly requiredBytes: number,
    public readonly availableBytes: number,
  ) {
    super(
      `SVG stash needs ~${formatMb(requiredBytes)} MB but only ${formatMb(availableBytes)} MB is available`,
    );
    this.name = 'SvgStashQuotaError';
  }
}

function formatMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/** Conservative ratio matching the PSD path — gzip text compresses
 *  far better than gzip bytes (typically 5-10× on SVG), but we
 *  reserve 50% headroom for layer-origin map + book-keeping. */
const QUOTA_OVERHEAD_RATIO = 0.5;

async function gzipCompressText(input: string): Promise<ArrayBuffer> {
  const stream = new Blob([input], { type: 'text/plain' })
    .stream()
    .pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

async function gzipDecompressText(input: ArrayBuffer): Promise<string> {
  const stream = new Blob([input]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
}

/** Returns the remaining IndexedDB quota in bytes, or `Infinity` if
 *  the browser doesn't surface storage estimates. Matches the PSD
 *  path's helper — could be hoisted to a shared module if a third
 *  format consumer appears. */
async function availableQuotaBytes(): Promise<number> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return Number.POSITIVE_INFINITY;
  }
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (typeof quota !== 'number' || typeof usage !== 'number') {
      return Number.POSITIVE_INFINITY;
    }
    return Math.max(0, quota - usage);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/** Persist an SVG stash entry. Compresses the text, runs a quota
 *  pre-check, writes to `svg-stashes` keyed by documentId. Throws
 *  {@link SvgStashQuotaError} on insufficient budget. */
export async function saveSvgStash(stash: {
  documentId: string;
  svgText: string;
  layerOriginById: Record<string, SvgNodeOrigin>;
  originalWidth: number;
  originalHeight: number;
}): Promise<void> {
  // Size estimate before compression: 2 bytes/char as a worst-case
  // for non-ASCII content (the resulting Uint8Array would be ~1
  // byte/char for the typical ASCII-heavy SVG, but we want to
  // pre-check the worst case).
  const estimatedBytes = stash.svgText.length * 2;
  const required = estimatedBytes * (1 + QUOTA_OVERHEAD_RATIO);
  const available = await availableQuotaBytes();
  if (available < required && Number.isFinite(available)) {
    throw new SvgStashQuotaError(required, available);
  }

  const svgTextGz = await gzipCompressText(stash.svgText);
  const record: StoredSvgStash = {
    documentId: stash.documentId,
    svgTextGz,
    // Encode the actual uncompressed byte size (UTF-8 encoded) so
    // surfaced messages match what the user would see if they saved
    // the SVG locally.
    uncompressedSize: new TextEncoder().encode(stash.svgText).byteLength,
    layerOriginById: stash.layerOriginById,
    originalWidth: stash.originalWidth,
    originalHeight: stash.originalHeight,
    savedAt: Date.now(),
  };

  const db = await openImageEditorDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SESSION_STORE_NAMES.svgStashes, 'readwrite');
    const store = tx.objectStore(SESSION_STORE_NAMES.svgStashes);
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/** Load a stash entry by documentId. Returns null if absent.
 *  Decompresses on read so callers see usable SVG text. */
export async function loadSvgStash(documentId: string): Promise<SvgStash | null> {
  const db = await openImageEditorDb();
  const record = await new Promise<StoredSvgStash | null>((resolve) => {
    const tx = db.transaction(SESSION_STORE_NAMES.svgStashes, 'readonly');
    const store = tx.objectStore(SESSION_STORE_NAMES.svgStashes);
    const request = store.get(documentId);
    request.onsuccess = () => resolve((request.result as StoredSvgStash | undefined) ?? null);
    request.onerror = () => {
      console.warn('[image-editor] loadSvgStash failed:', request.error);
      resolve(null);
    };
  });
  if (!record) return null;
  const svgText = await gzipDecompressText(record.svgTextGz);
  return {
    documentId: record.documentId,
    svgText,
    uncompressedSize: record.uncompressedSize,
    compressedSize: record.svgTextGz.byteLength,
    layerOriginById: record.layerOriginById,
    originalWidth: record.originalWidth,
    originalHeight: record.originalHeight,
    savedAt: record.savedAt,
  };
}

/** Returns true if a stash entry exists for the given documentId.
 *  Cheap presence check without the gzip-decompression round trip. */
export async function hasSvgStash(documentId: string): Promise<boolean> {
  const db = await openImageEditorDb();
  return new Promise<boolean>((resolve) => {
    const tx = db.transaction(SESSION_STORE_NAMES.svgStashes, 'readonly');
    const store = tx.objectStore(SESSION_STORE_NAMES.svgStashes);
    const request = store.count(documentId);
    request.onsuccess = () => resolve(request.result > 0);
    request.onerror = () => resolve(false);
  });
}

/** Delete stash entries whose documentId doesn't match any active
 *  session record. Best-effort cleanup intended for editor startup
 *  — matches the PSD path's `gcOrphanedStashes` semantics. */
export async function gcOrphanedSvgStashes(activeDocumentIds: Iterable<string>): Promise<number> {
  const live = new Set(activeDocumentIds);
  const db = await openImageEditorDb();
  return new Promise<number>((resolve) => {
    let removed = 0;
    const tx = db.transaction(SESSION_STORE_NAMES.svgStashes, 'readwrite');
    const store = tx.objectStore(SESSION_STORE_NAMES.svgStashes);
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      const record = cursor.value as StoredSvgStash;
      if (!live.has(record.documentId)) {
        cursor.delete();
        removed += 1;
      }
      cursor.continue();
    };
    tx.oncomplete = () => resolve(removed);
    tx.onerror = () => {
      console.warn('[image-editor] gcOrphanedSvgStashes failed:', tx.error);
      resolve(removed);
    };
  });
}
